import { useState } from 'react';
import {
  Badge,
  Box,
  Collapse,
  Group,
  Paper,
  Stack,
  Table,
  Text,
  ThemeIcon,
  UnstyledButton,
} from '@mantine/core';
import { PiCaretDown, PiCaretRight, PiCheck } from 'react-icons/pi';
import { formatPrice, semanticJurisdiction } from '@/lib/helpers';
import type {
  TCustomerSelections,
  TSelectionBlock,
  TSelectionProposal,
  TSelectionService,
} from '@/models/offers';

function ServiceTable({ services }: { services: TSelectionService[] }) {
  const selected = services.filter((s) => s.selected);

  if (selected.length === 0) return null;

  return (
    <Table striped highlightOnHover maw={760}>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Service</Table.Th>
          <Table.Th ta="right">Price</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {selected.map((svc) => (
          <ServiceRow key={svc.serviceId} service={svc} />
        ))}
      </Table.Tbody>
    </Table>
  );
}

function ServiceRow({ service }: { service: TSelectionService }) {
  return (
    <>
      <Table.Tr>
        <Table.Td>
          <Text fz="sm">{service.serviceName}</Text>
          {service.renewal && (
            <Text fz="xs" c="dimmed">
              {service.renewal.period}:{' '}
              {formatPrice(
                service.renewal.renewalPrice,
                service.renewal.renewalCurrency,
              )}
            </Text>
          )}
        </Table.Td>
        <Table.Td ta="right">
          <Group gap={6} justify="flex-end" wrap="nowrap">
            <Text fz="sm">
              {formatPrice(service.priceAmount, service.priceCurrency)}
            </Text>
            <ThemeIcon size="sm" variant="light" color="green">
              <PiCheck size={14} />
            </ThemeIcon>
          </Group>
        </Table.Td>
      </Table.Tr>
      {service.children.map((child) => (
        <Table.Tr key={child.serviceId}>
          <Table.Td pl="xl">
            <Text fz="xs" c="dimmed">
              {child.serviceName}
            </Text>
          </Table.Td>
          <Table.Td />
        </Table.Tr>
      ))}
    </>
  );
}

function CollapsibleSection({
  title,
  badge,
  defaultOpen = true,
  children,
}: {
  title: string;
  badge?: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Box>
      <UnstyledButton onClick={() => setOpen((o) => !o)} w="100%">
        <Group gap="xs" mb={4}>
          {open ? <PiCaretDown size={14} /> : <PiCaretRight size={14} />}
          <Text fz="sm" fw={600}>
            {title}
          </Text>
          {badge}
        </Group>
      </UnstyledButton>
      <Collapse expanded={open}>
        <Box pl="md">{children}</Box>
      </Collapse>
    </Box>
  );
}

function BlockSection({ block }: { block: TSelectionBlock }) {
  const hasSelected = block.services.some((s) => s.selected);
  if (!hasSelected) return null;

  return (
    <Box>
      <Group gap="xs" mb={4}>
        <Text fz="sm" fw={500}>
          {block.title}
        </Text>
        {block.selectedTier != null && (
          <Badge size="xs" variant="outline">
            {block.selectedPackageName ||
              block.tierNames[block.selectedTier] ||
              `Tier ${block.selectedTier}`}
          </Badge>
        )}
      </Group>
      <ServiceTable services={block.services} />
    </Box>
  );
}

function ProposalSection({ proposal }: { proposal: TSelectionProposal }) {
  const selectedCount = proposal.blocks.reduce(
    (sum, b) => sum + b.services.filter((s) => s.selected).length,
    0,
  );
  if (selectedCount === 0) return null;

  return (
    <CollapsibleSection
      title={proposal.cpName}
      badge={
        <Group gap={4}>
          <Text fz="xs" c="dimmed">
            {semanticJurisdiction(proposal.jurisdiction)}
          </Text>
          <Badge size="xs" variant="light">
            {proposal.currency}
          </Badge>
          <Badge size="xs" variant="light" color="gray">
            {selectedCount} services
          </Badge>
        </Group>
      }
    >
      <Stack gap="sm">
        {proposal.blocks.map((block) => (
          <BlockSection key={block.uuid} block={block} />
        ))}
      </Stack>
    </CollapsibleSection>
  );
}

export default function CustomerSelectionsPanel({
  selections,
}: {
  selections: TCustomerSelections;
}) {
  const [open, setOpen] = useState(true);

  const totalSelected =
    selections.proposals.reduce(
      (sum, p) =>
        sum +
        p.blocks.reduce(
          (bs, b) => bs + b.services.filter((s) => s.selected).length,
          0,
        ),
      0,
    ) + selections.standaloneServices.filter((s) => s.selected).length;

  const hasStandalone = selections.standaloneServices.some((s) => s.selected);

  return (
    <Paper withBorder p="sm">
      <Stack gap="md">
        <UnstyledButton onClick={() => setOpen((o) => !o)} w="100%">
          <Group justify="space-between">
            <Group gap="xs">
              {open ? <PiCaretDown size={14} /> : <PiCaretRight size={14} />}
              <Text fw={500} fz="sm">
                Customer Selections
              </Text>
              <Badge size="xs" variant="light" color="gray">
                {totalSelected} selected
              </Badge>
            </Group>
            <Badge
              size="sm"
              color={selections.source === 'snapshot' ? 'green' : 'yellow'}
            >
              {selections.source === 'snapshot' ? 'Final' : 'In Progress'}
            </Badge>
          </Group>
        </UnstyledButton>

        <Collapse expanded={open}>
          <Stack gap="md">
            <Group gap="md">
              {selections.clientType && (
                <Badge size="xs" variant="light">
                  {selections.clientType}
                </Badge>
              )}
              {selections.paymentMethod && (
                <Text fz="xs" c="dimmed">
                  Payment: {selections.paymentMethod}
                </Text>
              )}
              {selections.paymentCurrency && (
                <Text fz="xs" c="dimmed">
                  Currency: {selections.paymentCurrency}
                </Text>
              )}
              {selections.dueConversionRate != null &&
                selections.dueConversionRate !== 1 && (
                  <Text fz="xs" c="dimmed">
                    Rate: {selections.dueConversionRate} (
                    {selections.invoiceCurrency})
                  </Text>
                )}
            </Group>

            {totalSelected === 0 ? (
              <Text fz="sm" c="dimmed" ta="center" py="md">
                No selections yet.
              </Text>
            ) : (
              <Stack gap="md">
                {selections.proposals.map((proposal) => (
                  <ProposalSection key={proposal.cpId} proposal={proposal} />
                ))}

                {hasStandalone && (
                  <CollapsibleSection
                    title="Additional Services"
                    badge={
                      <Badge size="xs" variant="light" color="gray">
                        {
                          selections.standaloneServices.filter(
                            (s) => s.selected,
                          ).length
                        }{' '}
                        services
                      </Badge>
                    }
                  >
                    <ServiceTable services={selections.standaloneServices} />
                  </CollapsibleSection>
                )}
              </Stack>
            )}
          </Stack>
        </Collapse>
      </Stack>
    </Paper>
  );
}
